import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import axiosInstance from '../../variables/api/axiosInstance';
import { decouperDossiers, dossiersMarques } from './utils';
import i18n from '../../i18n';

/**
 * Numéros de sinistre cités dans une discussion : détection, puis ouverture du
 * dossier correspondant.
 *
 * <h3>Deux nomenclatures, pas une</h3>
 * Le backend (`SinistreServiceImpl`) génère deux formes selon le lieu du sinistre :
 *   - survenu au Togo   : « 2026/0234/BF »            → ANNÉE/ORDRE/PAYS
 *   - survenu à l'étranger : « NS/0234/BF/2026 »      → CODE_CIE/ORDRE/PAYS/ANNÉE
 *
 * Le code de compagnie peut contenir un espace - « AGIC NG » - parce que rien
 * n'interdit l'espace dans `organisme.code`. C'est un défaut du référentiel, mais
 * ces numéros existent en base : les ignorer reviendrait à ne pas reconnaître une
 * partie des dossiers réellement cités.
 *
 * L'alternative met la forme longue EN PREMIER : sinon la forme courte
 * capturerait « 0092 / BF » au milieu de la longue et couperait le numéro en deux.
 *
 * Les espaces autour des barres restent tolérés à la DÉTECTION : les messages
 * déjà écrits, et les documents imprimés avant le recollage des numéros, portent
 * encore l'ancienne forme espacée. On la reconnaît, on ne la produit plus.
 */
const ORDRE = '\\d{3,5}';
const PAYS = '[A-Z]{2}';
const ANNEE = '(?:19|20)\\d{2}';
const CODE_CIE = '[A-Z0-9]{1,12}(?: [A-Z0-9]{1,12})?';
const S = '\\s*/\\s*';

export const REGEX_NUMERO_SINISTRE = new RegExp(
  `(?:${CODE_CIE}${S}${ORDRE}${S}${PAYS}${S}${ANNEE}`
  + `|${ANNEE}${S}${ORDRE}${S}${PAYS}(?!${S}${ANNEE}))`,
  'g',
);

/**
 * Forme canonique « A/B/C » : deux écritures du même numéro doivent se confondre.
 *
 * La barre est le seul séparateur - c'est la nomenclature que le générateur
 * applique désormais (`NumeroSinistre` côté backend) et à laquelle le stock a été
 * ramené. Les espaces qu'on rencontre encore viennent de documents imprimés à
 * l'ancienne forme ou d'une recopie à la main ; ils se perdent ici.
 */
export const normaliserNumero = (v) => (v || '')
  .split('/').map((p) => p.replace(/\s+/g, '')).filter(Boolean).join('/');

/**
 * Découpe un texte en fragments { type: 'texte' | 'sinistre', valeur }.
 * À appliquer sur les fragments TEXTE de `decouperLiens`, jamais sur une URL :
 * une adresse contient des barres obliques et des chiffres.
 */
export const decouperNumerosSinistre = (texte = '') => {
  const parts = [];
  let dernier = 0;
  texte.replace(REGEX_NUMERO_SINISTRE, (num, index) => {
    if (index > dernier) parts.push({ type: 'texte', valeur: texte.slice(dernier, index) });
    parts.push({ type: 'sinistre', valeur: num.trim() });
    dernier = index + num.length;
    return num;
  });
  if (dernier < texte.length) parts.push({ type: 'texte', valeur: texte.slice(dernier) });
  return parts;
};

/**
 * Dossiers distincts cités dans une liste de messages, du plus récent au plus ancien.
 *
 * Un dossier désigné par marqueur (`#` dans la saisie) porte son identifiant : il
 * s'ouvre directement. Un numéro écrit à la main n'a que son numéro, résolu au clic.
 * Le marqueur est retiré du texte avant la détection des numéros bruts, sinon le
 * même dossier apparaîtrait deux fois - une fois avec, une fois sans identifiant.
 */
export const numerosCites = (messages = []) => {
  const vus = new Map();
  for (let i = messages.length - 1; i >= 0; i -= 1) {
    const m = messages[i];
    if (m?.supprime || !m?.contenu) continue;
    dossiersMarques(m.contenu).forEach((d) => {
      const cle = normaliserNumero(d.numero);
      if (!vus.has(cle) || !vus.get(cle).sinistreTrackingId) {
        vus.set(cle, { numero: cle, sinistreTrackingId: d.sinistreTrackingId, auteur: m.auteurNom, date: m.creeLe });
      }
    });
    const sansMarqueurs = decouperDossiers(m.contenu)
      .filter((f) => f.type === 'texte').map((f) => f.valeur).join(' ');
    (sansMarqueurs.match(REGEX_NUMERO_SINISTRE) || []).forEach((num) => {
      const cle = normaliserNumero(num);
      if (!vus.has(cle)) vus.set(cle, { numero: cle, auteur: m.auteurNom, date: m.creeLe });
    });
  }
  return [...vus.values()];
};

/**
 * Cherche des dossiers sinistres par numéro ou par mot-clé.
 *
 * Distinct de la recherche DANS la discussion : celle-ci ne trouve que ce que
 * quelqu'un a effectivement écrit. Chercher « 0363 » sans qu'aucun message ne le
 * mentionne ne devait pas rester sans réponse - le dossier existe, il est juste
 * ailleurs que dans le fil.
 */
export const chercherDossiers = async (q, taille = 6) => {
  const terme = (q || '').trim();
  if (terme.length < 2) return [];
  try {
    const r = await axiosInstance.get('/v1/sinistres', {
      params: { page: 0, size: taille, search: terme },
      skipErrorToast: true,
    });
    const liste = r.data?.data;
    return Array.isArray(liste) ? liste : [];
  } catch {
    // Sans habilitation SINISTRE_READ, la section reste simplement vide.
    return [];
  }
};

/**
 * Ouvre le dossier portant ce numéro.
 *
 * Le numéro n'est pas un identifiant : il faut le résoudre. Une seule
 * correspondance mène au dossier ; zéro ou plusieurs mènent à la liste
 * pré-filtrée, où l'on voit ce qui a été trouvé. Ouvrir arbitrairement le
 * premier de plusieurs homonymes serait pire que ne rien ouvrir.
 */
export const useOuvrirDossierSinistre = () => {
  const navigate = useNavigate();
  return useCallback(async (numero) => {
    const q = normaliserNumero(numero);
    try {
      const r = await axiosInstance.get('/v1/sinistres', {
        params: { page: 0, size: 5, search: q },
        skipErrorToast: true,
      });
      const liste = r.data?.data;
      const trouves = Array.isArray(liste) ? liste : [];
      const exacts = trouves.filter((s) => normaliserNumero(s.numeroSinistreLocal) === q);
      const cible = exacts.length === 1 ? exacts[0] : (trouves.length === 1 ? trouves[0] : null);
      if (cible?.sinistreTrackingId) {
        navigate(`/sinistres/${cible.sinistreTrackingId}`);
        return;
      }
      if (!trouves.length) toast.info(i18n.t('conversations:liens.aucunDossier', { numero: q }));
      navigate(`/sinistres?search=${encodeURIComponent(q)}`);
    } catch {
      // Habilitation manquante ou service indisponible : la liste dira laquelle.
      navigate(`/sinistres?search=${encodeURIComponent(q)}`);
    }
  }, [navigate]);
};
